// Every user-facing string of the UI, in French. Keys are flat, dot-separated, grouped by panel.
// t(key, vars) interpolates {name} placeholders; a value may be {one, other} for plurals (chosen by
// vars.n, French rule: 0 and 1 are singular). Panels built later add their own strings with
// registerStrings({...}) (see docs/UI-CONTRACT.md) — they never edit this file.

/** Month and day names (index 0 = January / Sunday). */
export const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
export const DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
export const DAYS_SHORT = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

const dict = {
  // Common
  'common.loading': 'Chargement…',
  'common.optional': 'facultatif',
  'common.close': 'Fermer',
  'common.moreInfo': 'En savoir plus',
  'common.cancel': 'Annuler',
  'common.confirm': 'Confirmer',
  'common.save': 'Enregistrer',
  'common.saved': 'Enregistré.',
  'common.delete': 'Supprimer',
  'common.edit': 'Modifier',
  'common.retry': 'Réessayer',
  'common.ok': 'OK',
  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.open': 'Ouvrir',
  'common.back': 'Retour',
  'common.more': 'Voir plus',
  'common.none': 'Aucun',
  'common.all': 'Tout',
  'common.copy': 'Copier',
  'common.copied': 'Copié dans le presse-papiers.',
  'common.search': 'Rechercher',
  'common.required': 'Ce champ est obligatoire.',
  'common.error': 'Une erreur est survenue.',
  'common.soon': 'Bientôt disponible',

  // App shell
  'app.name': 'Maeck Office',
  'app.wordmark': 'MAECK OFFICE',
  'app.tagline': 'Le bureau de vos agents IA',
  'app.skip': 'Aller aux tâches',
  'app.bootError': 'Impossible de joindre le serveur.',
  'app.bootErrorHint': 'Vérifiez que Maeck Office tourne sur le Mac mini, puis réessayez.',
  'app.degraded': 'Les données du bureau ne sont pas encore disponibles sur ce serveur.',
  'app.sessionExpired': 'Votre session a expiré. Reconnectez-vous.',
  'app.adminOnly': 'Réservé à l’administrateur.',
  'app.atelierDisabled': 'L’Atelier n’est pas activé sur ce serveur.',
  'app.personalDisabled': 'La bibliothèque de connecteurs n’est pas activée sur ce serveur.',
  'app.noFloors.title': 'Aucun étage visible',
  'app.noFloors.text': 'Aucun étage ne vous est encore ouvert. Demandez un accès à l’administrateur.',
  'app.noFloors.admin': 'Créez un premier étage dans Administration → Étages & services.',
  'app.emptyFloor.title': 'Cet étage n’a pas encore de service',
  'app.emptyFloor.text': 'Ajoutez des services et des agents dans Administration → Étages & services.',
  'app.emptyFloor.admin': 'Créez son premier service ici, puis ajoutez-lui des agents depuis sa carte (+).',
  'oa.floor.new': 'NOUVEL ÉTAGE',
  'oa.dept.new': 'NOUVEAU SERVICE',
  'app.sceneFallback': 'Vue 3D indisponible sur cet appareil : affichage simplifié.',
  'app.mockBadge': 'MODE DÉMO',
  'app.mockTitle': 'Moteur factice : aucun appel IA, aucun coût.',
  'app.unknownFloor': 'Cet étage n’existe pas ou ne vous est pas ouvert.',
  'app.resynced': 'Connexion rétablie.',
  'app.connectionLost': 'Connexion perdue — reconnexion…',
  'app.updateAvailable': 'Une nouvelle version du bureau est en ligne.',
  'app.updateReload': 'Recharger',
  'app.sceneLabel': 'Vue 3D de l’étage',

  // Task states (badges)
  'state.scheduled': 'PROGRAMMÉE',
  'state.queued': 'EN ATTENTE',
  'state.running': 'EN COURS',
  'state.executing': 'EXÉCUTION',
  'state.awaiting_approval': 'À VALIDER',
  'state.done': 'FAITE',
  'state.blocked': 'BLOQUÉE',
  'state.todo': 'À RÉGLER',
  'state.failed': 'ÉCHEC',
  'state.rejected': 'REFUSÉE',
  'state.cancelled': 'ANNULÉE',
  'state.ignored': 'IGNORÉE',
  'state.resolved': 'RÉSOLUE ENSUITE',

  // Agent live status
  'agentStatus.idle': 'Disponible',
  'agentStatus.working': 'Au travail',
  'agentStatus.waiting': 'Attend une validation',
  'agentStatus.error': 'En erreur',
  'agentStatus.done': 'Vient de terminer',

  // Access levels and roles
  'role.admin': 'ADMINISTRATEUR',
  'role.member': 'MEMBRE',
  'level.view': 'Lecture',
  'level.operate': 'Opérateur',
  'level.manage': 'Responsable',
  'level.admin': 'Administrateur',

  // Engines
  'engine.claude': 'CLAUDE',
  'engine.codex': 'CODEX',
  'engine.openai-image': 'IMAGES',
  'engine.gemini': 'GEMINI',
  'engine.kie': 'KIE.AI',
  'engine.mock': 'DÉMO',
  'engine.name.claude': 'Claude',
  'engine.name.codex': 'Codex (ChatGPT)',
  'engine.name.openai-image': 'Images OpenAI',
  'engine.name.gemini': 'Gemini',
  'engine.name.kie': 'Kie.ai (génération de vidéos)',
  'engine.name.mock': 'Simulation',
  'engine.ok': '{name} : connecté{version}',
  'engine.ko': '{name} : indisponible{error}',
  'engine.disconnected': '{name} : non connecté{error}',
  'engine.unknown': '{name} : connexion non vérifiée{error}',
  'engine.pending': '{name} : vérification en cours…',

  // Login / setup / forced password change
  'login.title': 'Connexion',
  'login.subtitle': 'Connectez-vous pour retrouver votre bureau.',
  'login.username': 'Identifiant',
  'login.password': 'Mot de passe',
  'login.submit': 'SE CONNECTER',
  'login.submitting': 'Connexion…',
  'login.invalid': 'Identifiant ou mot de passe incorrect',
  'login.locked': 'Trop de tentatives. Réessayez dans {duration}.',
  'login.unlocked': 'Vous pouvez réessayer.',
  'login.showPassword': 'Afficher le mot de passe',
  'login.hidePassword': 'Masquer le mot de passe',
  'login.footer': 'Accès réservé. Les connexions sont journalisées.',
  'login.illustration': 'Un petit immeuble de bureaux en perspective, un service coloré par étage',
  'login.setupLink': 'Première installation ?',
  'setup.title': 'Première installation',
  'setup.subtitle': 'Créez le compte administrateur. Le jeton d’installation est affiché une seule fois dans le journal du serveur, avec l’adresse …/#/setup.',
  'setup.token': 'Jeton d’installation',
  'setup.username': 'Identifiant',
  'setup.usernameHint': '2 à 32 caractères : minuscules, chiffres, point, tiret, tiret bas.',
  'setup.usernameInvalid': 'Identifiant invalide : 2 à 32 caractères parmi a-z, 0-9, « . », « - », « _ ».',
  'setup.displayName': 'Nom affiché',
  'setup.password': 'Mot de passe',
  'setup.confirm': 'Confirmer le mot de passe',
  'setup.submit': 'CRÉER LE COMPTE ADMINISTRATEUR',
  'setup.done': 'Compte administrateur créé. Bienvenue !',
  'setup.notRequired': 'Un administrateur existe déjà. Connectez-vous.',
  'setup.backToLogin': 'Retour à la connexion',
  'change.title': 'Nouveau mot de passe',
  'change.subtitle': 'Votre mot de passe est temporaire. Choisissez-en un nouveau pour continuer.',
  'change.current': 'Mot de passe actuel (temporaire)',
  'change.currentShort': 'Mot de passe actuel',
  'change.next': 'Nouveau mot de passe',
  'change.confirm': 'Confirmer le nouveau mot de passe',
  'change.submit': 'ENREGISTRER',
  'change.done': 'Mot de passe modifié.',
  'change.logout': 'Se déconnecter',
  'password.rule.length': '10 caractères minimum',
  'password.rule.mix': 'Pas uniquement des lettres ni uniquement des chiffres',
  'password.rule.username': 'Différent de l’identifiant',
  'password.mismatch': 'Les deux mots de passe ne correspondent pas.',
  'password.weak': 'Le mot de passe ne respecte pas les règles ci-dessous.',

  // Top bar
  'topbar.home': 'Maeck Office — vue immeuble',
  'topbar.connectedTo': 'CONNECTÉ À',
  'topbar.connectors': 'Connecteurs',
  'topbar.noConnectors': 'Aucun connecteur',
  'topbar.more': '+{n}',
  'topbar.moreTitle': '{n} autres connecteurs',
  'topbar.moreAria': { one: '{n} autre connecteur : voir la liste', other: '{n} autres connecteurs : voir la liste' },
  'topbar.moreList': 'Autres connecteurs',
  'topbar.bothEngines': 'Claude et Codex',
  'topbar.engines': 'Moteurs',
  'topbar.floorMenu': 'Actions sur l’étage {name} : renommer, nouveau service, nouvel étage',
  'topbar.floorMenuTitle': 'Renommer l’étage, ajouter un service ou un étage',
  'topbar.buildingMenu': 'Actions sur l’immeuble : nouvel étage',
  'topbar.buildingMenuTitle': 'Ajouter un étage',
  'topbar.approvals': 'À VALIDER',
  'topbar.approvalsAria': { one: '{n} tâche à valider', other: '{n} tâches à valider' },
  'topbar.calendar': 'CALENDRIER',
  'topbar.automations': 'AUTOMATISATIONS',
  'topbar.suggestions': 'SUGGESTIONS',
  'topbar.studio': 'STUDIO',
  'topbar.studioTitle': 'Studio de l’étage : les pubs en production et à valider',
  'topbar.studioAria': 'Studio de l’étage {name}',
  'topbar.suggestionsAria': { one: '{n} suggestion à examiner', other: '{n} suggestions à examiner' },
  'topbar.userMenu': 'Menu du compte de {name}',
  'topbar.clock': 'Heure locale',
  'topbar.live.open': 'En direct',
  'topbar.live.connecting': 'Connexion au direct…',
  'topbar.live.reconnecting': 'Reconnexion…',
  'topbar.live.offline': 'Hors ligne — nouvelle tentative…',
  'topbar.live.closed': 'Direct arrêté',
  'topbar.live.idle': 'Direct en attente',
  'topbar.live.unsupported': 'Temps réel non pris en charge par ce navigateur',
  'connector.status.connected': 'connecté',
  'connector.status.needs-auth': 'authentification requise',
  'connector.status.failed': 'échec de connexion',
  'connector.status.unknown': 'état inconnu',
  // A connector of the office's library (« Bibliothèque de connecteurs »): its own states.
  'connector.status.added': 'à connecter',
  'connector.status.error': 'en erreur',
  'connector.addedBy': 'Ajouté par {name}',
  'connector.everyFloor': 'Sur tous les étages',
  'connector.tools': '{read} outil(s) en lecture · {write} en écriture',
  'connector.usedBy': 'Utilisé sur cet étage par : {list}',
  'connector.unused': 'Aucun service de cet étage ne l’utilise.',
  'connector.manage': 'Gérer les connecteurs',
  'connector.openLibrary': 'Voir dans « Connecteurs »',
  'connector.chipAria': '{name}, {status}',

  // User menu, profile, sessions
  'menu.profile': 'Mon profil',
  'menu.password': 'Changer le mot de passe',
  'menu.sessions': 'Sessions ouvertes',
  'menu.connectors': 'Connecteurs',
  'menu.mcp': 'Mon accès Claude (MCP)',
  'menu.themeDark': 'Thème sombre',
  'menu.themeLight': 'Thème clair',
  'menu.shortcuts': 'Raccourcis clavier',
  'menu.calendar': 'Calendrier',
  'menu.board': 'Tableau des tâches',
  'menu.automations': 'Automatisations',
  'menu.suggestions': 'Suggestions',
  'menu.admin': 'Administration',
  'menu.atelier': 'Atelier',
  'menu.logout': 'Se déconnecter',
  'profile.title': 'Mon profil',
  'profile.username': 'Identifiant',
  'profile.displayName': 'Nom affiché',
  'profile.role': 'Rôle',
  'profile.lastLogin': 'Dernière connexion',
  'profile.createdAt': 'Compte créé',
  'profile.grants': 'Accès',
  'profile.allFloors': 'Tous les étages et services (administrateur).',
  'profile.noGrants': 'Aucun accès pour le moment. Demandez-en un à l’administrateur.',
  'profile.allDepts': 'tous les services',
  'profile.grantLine': '{floor} · {dept}',
  'profile.connectors': 'Connecteurs du bureau',
  'profile.connectorsAdmin': 'Tous : un administrateur n’est jamais limité.',
  'profile.connectorsAll': 'Tous les connecteurs autorisés sur vos étages.',
  'profile.connectorsExcept': 'Tous, sauf : {list}.',
  'profile.connectorsOnly': 'Seulement : {list}.',
  'profile.connectorsNone': 'Aucun : vos demandes se font sans connecteur extérieur.',
  'profile.connectorsHint': 'Réglé par l’administrateur. Pour vos demandes (tâches, discussions, validations, routines que vous modifiez) et pour votre Claude branché sur le bureau, les agents ne se servent que de ceux-là, connecteurs ajoutés dans le bureau compris. Les Tables du bureau ne sont pas concernées.',
  'profile.mcp': 'Accès Claude (MCP)',
  'profile.mcpText': 'Branchez votre propre Claude sur le bureau, avec vos accès.',
  'profile.mcpOpen': 'MON ACCÈS CLAUDE (MCP)',
  // Connector policy summaries (web/ui/connectorpolicy.js policySummary)
  'policy.all': 'tous',
  'policy.except': 'tous sauf {list}',
  'policy.only': 'seulement {list}',
  'policy.none': 'aucun',
  'policy.libraryGone': 'connecteur retiré',
  'sessions.title': 'Sessions ouvertes',
  'sessions.subtitle': 'Les appareils connectés à votre compte. Révoquez ceux que vous ne reconnaissez pas.',
  'sessions.current': 'CETTE SESSION',
  'sessions.revoke': 'Révoquer',
  'sessions.revoked': 'Session révoquée.',
  'sessions.empty': 'Aucune session ouverte.',
  'sessions.created': 'Ouverte {when}',
  'sessions.lastSeen': 'Dernière activité {when}',
  'sessions.unknownDevice': 'Appareil inconnu',
  'sessions.confirmTitle': 'Révoquer la session ?',
  'sessions.confirmText': 'L’appareil concerné devra se reconnecter.',
  'sessions.unavailable': 'La liste des sessions n’est pas disponible.',
  'device.iphone': 'iPhone',
  'device.ipad': 'iPad',
  'device.android': 'Android',
  'device.mac': 'Mac',
  'device.windows': 'Windows',
  'device.linux': 'Linux',
  'browser.safari': 'Safari',
  'browser.chrome': 'Chrome',
  'browser.firefox': 'Firefox',
  'browser.edge': 'Edge',
  'logout.done': 'Vous êtes déconnecté.',
  'theme.dark': 'Thème sombre activé.',
  'theme.light': 'Thème clair activé.',

  // Elevator and scene controls
  'elevator.label': 'Étages',
  'elevator.building': 'Vue immeuble',
  'elevator.floorAria': 'Étage {n} : {name}',
  'elevator.doing': '{n} en cours',
  'elevator.waiting': '{n} à valider',
  'elevator.calm': 'calme',
  'elevator.notices': { one: '{n} point à régler', other: '{n} points à régler' },
  'elevator.doneNotices': { one: '{n} demande terminée', other: '{n} demandes terminées' },
  'scene.controls': 'Contrôles de la vue',
  'scene.zoomIn': 'Zoomer',
  'scene.zoomOut': 'Dézoomer',
  'scene.reset': 'Recentrer la vue',

  // Department and floor cards
  'dept.agents': { one: 'AGENT', other: 'AGENTS' },
  'dept.waiting': 'À VALIDER',
  'dept.doneToday': 'FAITS AUJOURD’HUI',
  'dept.doing': 'EN COURS',
  'dept.next': 'À VENIR',
  'dept.routines': { one: '{n} routine', other: '{n} routines' },
  'dept.routinesTitle': { one: '{n} routine active dans ce service', other: '{n} routines actives dans ce service' },
  'dept.failed': 'ÉCHECS',
  'dept.cardAria': 'Service {name}, {n} agent(s). Filtrer les tâches sur ce service.',
  'dept.filterOn': 'Tâches filtrées sur « {name} »',
  'dept.filterOff': 'Filtre retiré',
  'dept.menuAria': 'Ajouter un agent au service {name}, ou le renommer',
  'dept.menuTitle': 'Ajouter un agent, renommer le service',
  'dept.studioAria': 'Ouvrir le studio du service {name}',
  'dept.studioTitle': 'Studio : les créations du service',
  'floor.depts': { one: 'SERVICE', other: 'SERVICES' },
  'floor.agents': { one: '{n} agent', other: '{n} agents' },
  'floor.cardAria': 'Étage {name} : ouvrir',

  // Name tags
  'agent.lead': 'Responsable du service',
  'agent.tagAria': '{name}, {role} — {status}',

  // Task panel
  'panel.label': 'Tâches',
  'panel.title': 'TÂCHES',
  'panel.scopeFloor': 'Tout l’étage',
  'panel.scopeBuilding': 'Tout l’immeuble',
  'panel.clearFilter': 'Retirer le filtre de service',
  'panel.collapse': 'Masquer le panneau',
  'panel.expand': 'Afficher les tâches',
  'panel.tabs.tasks': 'Tâches',
  'panel.tabs.waiting': 'À valider',
  'panel.sheetToggle': 'Agrandir ou réduire le panneau',
  'panel.board': 'Vue Kanban',
  'panel.boardTitle': 'Toutes les tâches en colonnes : à faire, en cours, à valider, en erreur… (T)',
  // Images joined to a message or a task (pièces jointes, web/ui/attachments.js)
  'attach.add': 'Joindre une image',
  'attach.addTitle': 'Joindre une image ({max} au plus : PNG, JPEG, WebP ou GIF, 10 Mo chacune). Vous pouvez aussi coller une capture.',
  'attach.trayLabel': 'Images jointes',
  'attach.remove': 'Retirer l’image {name}',
  'attach.tooMany': '{max} images au plus à la fois.',
  'attach.badType': '« {name} » n’est pas une image PNG, JPEG, WebP ou GIF.',
  'attach.tooBig': '« {name} » est trop lourde ({max} Mo au plus).',
  'attach.empty': '« {name} » est vide.',
  'attach.readFailed': 'L’image « {name} » n’a pas pu être lue.',
  'attach.drop': 'Déposez l’image ici',
  'filter.label': 'Filtrer les tâches',
  'filter.all': 'TOUT',
  'filter.running': 'EN COURS',
  'filter.waiting': 'À VALIDER',
  'filter.done': 'FAITS',
  'filter.blocked': 'BLOQUÉES',
  'filter.scheduled': 'PROGRAMMÉS',
  'tasks.listLabel': 'Liste des tâches',
  'tasks.empty.title': 'Aucune tâche pour le moment',
  'tasks.empty.text': 'Demandez une tâche au chef d’un service dans sa discussion (« Lancer comme tâche ») ou programmez-la dans le calendrier : elle apparaîtra ici.',
  'tasks.emptyFilter.title': 'Rien ici',
  'tasks.emptyFilter.text': 'Aucune tâche ne correspond à ce filtre.',
  'tasks.emptyWaiting.title': 'Rien à valider',
  'tasks.emptyWaiting.text': 'Les brouillons des agents apparaîtront ici avant de partir.',
  'tasks.unavailable': 'La liste des tâches n’est pas encore disponible sur ce serveur.',
  'tasks.loadMore': 'Charger plus',
  'tasks.loading': 'Chargement des tâches…',
  'tasks.openApprovals': 'Tout valider à la suite',
  'task.meta': '{agent} · {dept}',
  'task.due': 'prévue {when}',
  'task.late': 'EN RETARD',
  'task.progressAria': 'Avancement : {p} %',
  'task.revisions': { one: '{n} révision', other: '{n} révisions' },
  'task.cardAria': '{title} — {state}',
  'task.untitled': 'Tâche sans titre',
  'task.routing': 'Choix de l’agent…',
  'task.fileCount': { one: '{n} fichier', other: '{n} fichiers' },
  'task.attempt': '{nth} essai',
  'task.attempts': { one: '{n} essai', other: '{n} essais' },

  // Toasts and notifications
  'toast.close': 'Fermer la notification',
  'toast.region': 'Notifications',
  'notify.approval': 'À valider : {title}',
  'notify.done': 'Terminée : {title}',
  'notify.failed': 'Échec : {title}',
  'notify.open': 'Ouvrir',
  'notify.generic': 'Notification',

  // Keyboard shortcuts help
  'keys.title': 'Raccourcis clavier',
  'keys.board': 'Tableau des tâches (Kanban)',
  'keys.calendar': 'Calendrier',
  'keys.approvals': 'Tâches à valider',
  'keys.building': 'Vue immeuble / retour à l’étage',
  'keys.floors': 'Aller à l’étage 1 à 9',
  'keys.pages': 'Étage au-dessus / en dessous',
  'keys.theme': 'Thème clair / sombre',
  'keys.escape': 'Fermer le panneau ouvert',
  'keys.help': 'Afficher cette aide',

  // Overlay primitives
  'overlay.close': 'Fermer',
  'overlay.confirmTitle': 'Confirmer',
  'overlay.promptTitle': 'Saisir une valeur',

  // API errors
  'api.network': 'Connexion au serveur impossible. Vérifiez votre réseau.',
  'api.timeout': 'Le serveur met trop de temps à répondre.',
  'api.400': 'Requête invalide.',
  'api.401': 'Connexion requise.',
  'api.403': 'Accès refusé.',
  'api.404': 'Introuvable.',
  'api.409': 'Conflit : l’action est déjà en cours ou n’est plus possible.',
  'api.413': 'Contenu trop volumineux.',
  'api.415': 'Format non pris en charge.',
  'api.421': 'Adresse du serveur non autorisée.',
  'api.429': 'Trop de requêtes. Réessayez plus tard.',
  'api.500': 'Erreur interne du serveur.',
  'api.502': 'Le serveur ne répond pas (passerelle).',
  'api.503': 'Service momentanément indisponible.',
  'api.generic': 'Erreur {status}.',

  // Relative time and dates
  'time.justNow': 'à l’instant',
  'time.soon': 'dans un instant',
  'time.minAgo': 'il y a {n} min',
  'time.hourAgo': 'il y a {n} h',
  'time.dayAgo': 'il y a {n} j',
  'time.inMin': 'dans {n} min',
  'time.minShort': '{n} min',
  'time.hourShort': '{n} h',
  'time.dayShort': '{n} j',
  'time.yesterday': 'hier',
  'date.first': '1er',
  'date.todayAt': 'aujourd’hui à {time}',
  'date.yesterdayAt': 'hier à {time}',
  'date.tomorrowAt': 'demain à {time}',
  'date.dayAt': '{day} à {time}',
  'date.dateAt': '{date} à {time}',

  // Placeholders of the panels built later (stubs)
  'stub.title': 'Bientôt disponible',
  'stub.text': 'Ce panneau est en cours de construction. Il arrive dans une prochaine version.',
  'stub.task': 'Tâche',
  'stub.approvals': 'À valider',
  'stub.agent': 'Agent',
  'stub.calendar': 'Calendrier',
  'stub.automations': 'Automatisations',
  'stub.admin': 'Administration',
  'stub.atelier': 'Atelier',

  // Shared vocabulary for the panels built later (SPEC §11); they may add more via registerStrings.
  'taskdrawer.request': 'DEMANDE',
  'taskdrawer.source': 'ORIGINE',
  'taskdrawer.source.user': 'Demandée par {name}',
  'taskdrawer.source.routine': 'Routine « {name} »',
  'taskdrawer.source.trigger': 'Déclencheur n8n « {name} »',
  'taskdrawer.source.chat': 'Depuis une discussion',
  'taskdrawer.source.calendar': 'Depuis le calendrier',
  'taskdrawer.timeline': 'CHRONOLOGIE',
  'taskdrawer.draft': 'BROUILLON',
  'taskdrawer.result': 'RÉSULTAT',
  'taskdrawer.output': 'SORTIE JSON',
  'taskdrawer.files': 'FICHIERS',
  'taskdrawer.download': 'Télécharger',
  'taskdrawer.callback': 'RETOUR À N8N',
  'approval.approve': 'APPROUVER',
  'approval.reject': 'REFUSER',
  'approval.editDraft': 'Modifier le brouillon',
  'approval.feedback': 'Qu’est-ce qui ne va pas ?',
  'approval.remember': 'Retenir comme règle pour cet agent',
  'approval.rerun': 'Refaire',
  'approval.approved': 'Approuvée.',
  'approval.rejected': 'Refusée.',
  'task.cancel': 'Annuler la tâche',
  'task.retry': 'Relancer',
  'task.delete': 'Supprimer la tâche',
  'agentdrawer.tabs.overview': 'Aperçu',
  'agentdrawer.tabs.routines': 'Routines',
  'agentdrawer.tabs.chat': 'Discussion',
  'agentdrawer.tabs.tasks': 'Tâches',
  'agentdrawer.tabs.settings': 'Réglages',
  'agentdrawer.toTask': 'Transformer en tâche',
  'calendar.title': 'Calendrier',
  'calendar.week': 'SEMAINE',
  'calendar.month': 'MOIS',
  'calendar.today': 'AUJOURD’HUI',
  'calendar.routines': 'ROUTINES',
  'calendar.done': 'FAITES',
  'calendar.scheduled': 'PROGRAMMÉES',
  'automations.title': 'Automatisations',
  'automations.tabs.routines': 'Routines',
  'automations.tabs.triggers': 'Déclencheurs n8n',
  'automations.runNow': 'Lancer maintenant',
  'automations.pause': 'Mettre en pause',
  'automations.resume': 'Reprendre',
  'suggestions.title': 'Suggestions d’amélioration',
  'admin.title': 'Administration',
  'admin.tabs.users': 'Utilisateurs',
  'admin.tabs.floors': 'Étages & services',
  'admin.tabs.connectors': 'Connecteurs',
  'admin.tabs.engines': 'Moteurs',
  'admin.tabs.activity': 'Activité',
  'admin.tabs.audit': 'Journal',
  'atelier.title': 'Atelier',
  'atelier.placeholder': 'Que voulez-vous changer dans l’application ?',
  'atelier.test': 'TESTER',
  'atelier.apply': 'APPLIQUER ET DÉPLOYER',
  'atelier.discard': 'ABANDONNER',
};

/**
 * Translates `key`, interpolating {placeholders} from `vars`. Missing keys return the key itself.
 * @param {string} key
 * @param {Record<string, any>} [vars]
 * @returns {string}
 */
export function t(key, vars) {
  // Own keys only: dynamic keys ("state.<value from the server>") must never reach Object.prototype.
  let value = hasString(key) ? dict[key] : undefined;
  if (value === undefined || value === null) return String(key);
  if (typeof value === 'object' && value !== null) {
    const n = Math.abs(Number(vars?.n ?? vars?.count ?? 0));
    value = n < 2 ? value.one : value.other;
  }
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (m, name) => (vars[name] === undefined || vars[name] === null ? '' : String(vars[name])));
}

/** True when the dictionary has `key`. */
export function hasString(key) {
  return Object.prototype.hasOwnProperty.call(dict, key);
}

/**
 * Adds strings for a panel. Existing keys are kept unless `override` is true.
 * @param {Record<string, string|{one: string, other: string}>} strings
 * @param {{override?: boolean}} [opts]
 */
export function registerStrings(strings, { override = false } = {}) {
  for (const [k, v] of Object.entries(strings || {})) {
    if (k === '__proto__' || k === 'constructor' || k === 'prototype') continue;
    if (typeof v !== 'string' && !(v && typeof v === 'object' && typeof v.one === 'string' && typeof v.other === 'string')) continue;
    if (override || !hasString(k)) dict[k] = v;
  }
}

/** All keys (for tests and tooling). */
export function stringKeys() {
  return Object.keys(dict);
}
